import { transaction, reaction } from 'mobx';
import { mobxSet } from '@src/slide/infrastructure/module/independent/assert/get-set';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import AccessibilityConstants from '@src/slide/application/service/accessibility-manager/core/accessibility-constants';
import { SelectedEleMgrType } from '@src/slide/domain/selection/constant/select-element-type';
import { MODES } from '@src/slide/domain/ui-model/element-modeles/property/frame/frame.interface';
import { PPTEditorStateType } from '@src/slide/domain/ui-model/constant/edit-state-type';
import { IConnectorModel } from '@src/slide/domain/ui-model/element-modeles/Interface/connector-model.interface';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { IUserPriviledgeService } from '@src/slide/application/service/user-priviledge-service/user-priviledge-service.interface';
import { IMoveElementManager } from './move-element-manager.interface';
import { IMenuManager } from '@src/slide/application/service/menu-manager/menu-manager.interface';
import EditProcess from '@src/slide/application/service/edit-action-services/edit-process';
import type EditingStateManager from '@src/slide/application/service/editing-state-manager';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { IRect } from '@src/slide/infrastructure/module/independent/graphic/rect/rect';
import { IDynamicService } from '@src/slide/infrastructure/module/independent/dynamic-loader/dynamic-service';
import { IAccessibilityManager } from '@src/slide/application/service/accessibility-manager/core/accessibility-manager.interface';

export default class MoveElementManager implements IMoveElementManager {
    private moveTimer: ReturnType<typeof setTimeout> | null;
    private lastSelectArray: IElementBaseModel[] | null;
    private moveOffsetFrame: IRect | null;
    private currentMoveType: string | null;
    private editProcess: EditProcess;
    private a11yManager?: IDynamicService<IAccessibilityManager>;

    public constructor(
        private menuManagerRef: IMenuManager,
        private editingStateManagerRef: EditingStateManager,
        private selectionService: ISelectionService,
        private userPriviledgeService: IUserPriviledgeService,
    ) {
        this.moveTimer = null;
        this.lastSelectArray = null;
        this.moveOffsetFrame = null;
        this.currentMoveType = null;
        this.editProcess = new EditProcess(editingStateManagerRef);
        this.editProcess.setSelectionService(this.selectionService);

        setTimeout(() => {
            this.observerSelectElementsAndSlideChanged();
        }, 1000);
    }

    public setA11yManagerDelegate(a11yManager: IDynamicService<IAccessibilityManager>): void {
        this.a11yManager = a11yManager;
    }

    public checkAndMoveSelectedElementsWithArrow = (keyCode: number): boolean => {
        if (!this.menuManagerRef) {
            return false;
        }
        if (this.userPriviledgeService.pptEditorState === PPTEditorStateType.EDITABLE) {
            const groupSelectedUIModelArr = this.selectionService.selectedElements
                .selectedArrayForType(SelectedEleMgrType.GROUP) || [];
            if (groupSelectedUIModelArr.length > 0) {
                this.menuManagerRef.hideAllMenuNum += 1;
                this.currentMoveType = SelectedEleMgrType.GROUP;
                this.lastSelectArray = groupSelectedUIModelArr.map(model => model);
                this.moveSelectedElementsWithArrow(keyCode);
                return true;
            }
            const slideSelectedUIModelArr = this.selectionService.selectedElements
                .selectedArrayForType(SelectedEleMgrType.SLIDE) || [];
            if (slideSelectedUIModelArr.length > 0) {
                this.menuManagerRef.hideAllMenuNum += 1;
                this.currentMoveType = SelectedEleMgrType.SLIDE;
                this.lastSelectArray = slideSelectedUIModelArr.map(model => model);
                this.moveSelectedElementsWithArrow(keyCode);
                return true;
            }
        }
        return false;
    };


    public moveSelectedElementsWithArrow = (keyCode: number): void => {
        if (this.moveOffsetFrame === null) {
            this.moveOffsetFrame = {
                left: 0,
                top: 0,
                width: 0,
                height: 0,
            };
        }

        switch (keyCode) {
            case 37:
                this.moveOffsetFrame.left -= 1;
                break;
            case 39:
                this.moveOffsetFrame.left += 1;
                break;
            case 38:
                this.moveOffsetFrame.top -= 1;
                break;
            case 40:
                this.moveOffsetFrame.top += 1;
        }

        // 如果开启无障碍就派发事件
        this.a11yManager?.promise().then(a11yManager => a11yManager.isAccessibilityOn)
            .then((isAccessibilityOn) => {
                if (isAccessibilityOn) {
                    return import(/* webpackChunkName: "service" */ '@src/slide/application/service/accessibility-manager/core/accessibility');
                }
                return null;
            })
            .then((module) => {
                if (module) {
                    const Accessibility = module.default;
                    Accessibility.emit(AccessibilityConstants.MOVE_SELECTED_ELEMENTS_WITH_ARROW, this.moveOffsetFrame);
                }
            });

        this.updateOperationFrame(this.moveOffsetFrame);

        if (this.moveTimer) {
            clearTimeout(this.moveTimer);
        }

        this.moveTimer = setTimeout(() => {
            this.delayUpdatePropsForKeyMoveing();
        }, 300);
    };

    public checkIsDelayUpdatePosing = (): void => {
        if (this.moveTimer) {
            clearTimeout(this.moveTimer);
        }
        this.delayUpdatePropsForKeyMoveing();
    };

    private observerSelectElementsAndSlideChanged = () => {
        reaction(() => ({
            lengthSlide: this.selectionService.selectedElements.selectedArrayForType(SelectedEleMgrType.SLIDE).length,
            lengthGroup: this.selectionService.selectedElements.selectedArrayForType(SelectedEleMgrType.GROUP).length,
            currentSlideIndex: this.selectionService.currentSlideIndex,
        }), () => {
            if (this.moveTimer && this.moveOffsetFrame) {
                this.delayUpdatePropsForKeyMoveingWhileSelectChange();
            }
        }, { fireImmediately: false });
    };

    private delayUpdatePropsForKeyMoveing = () => {
        if (!this.lastSelectArray) {
            return;
        }
        if (this.currentMoveType === SelectedEleMgrType.GROUP) {
            const groupChild = this.selectionService.selectedElements
                .selectedArrayForType(SelectedEleMgrType.GROUP);
            if (groupChild?.[0]) {
                this.editProcess.persistSizeForGroup(groupChild[0].rootGroupModel);
                this.editProcess.updateOperationFrameForGroup(null);
            }
        } else {
            this.editProcess.persistSize(MODES.MOVE);
            this.editingStateManagerRef?.updateElementDraggingState(false);
            this.editProcess.updateOperationFrame(null);
        }
        this.clear();
    };

    /**
     * 在通过键盘快捷键移动时，可能在还未执行delayUpdatePropsForKeyMoveing时切换选中元素，此时需要更新之前选中元素的位置信息。
     */
    private delayUpdatePropsForKeyMoveingWhileSelectChange = () => {
        if (!this.lastSelectArray) {
            return;
        }
        if (this.currentMoveType === SelectedEleMgrType.GROUP) {
            const groupChild = this.selectionService.selectedElements
                .selectedArrayForType(SelectedEleMgrType.GROUP);
            if (this.lastSelectArray[0]) {
                this.editProcess.persistSizeForGroup(groupChild[0].rootGroupModel, false, this.lastSelectArray);
                this.editProcess.updateOperationFrameForGroup(null);
            }
        } else {
            this.editProcess.persistSize(MODES.MOVE, false, this.lastSelectArray);
            this.editingStateManagerRef?.updateElementDraggingState(false);
            this.editProcess.updateOperationFrame(null);
        }
        this.clear();
    };

    private updateOperationFrame = (offsetFrame) => {
        // eslint-disable-next-line complexity
        transaction(() => {
            const selectedUIModelArr = this.selectionService
                .selectedElements.selectedArrayForType(this.currentMoveType) || [];
            // eslint-disable-next-line @typescript-eslint/prefer-for-of
            for (let i = 0; i < selectedUIModelArr.length; i++) {
                const selectedUIModel = selectedUIModelArr[i];
                if (offsetFrame === null) {
                    mobxSet(selectedUIModel, 'operationFrame', null);
                } else {
                    mobxSet(selectedUIModel, 'operationFrame', {
                        left: selectedUIModel.props.style.left + offsetFrame.left,
                        top: selectedUIModel.props.style.top + offsetFrame.top,
                        width: selectedUIModel.props.style.width + offsetFrame.width,
                        height: selectedUIModel.props.style.height + offsetFrame.height,
                    });
                }
                if (this.editingStateManagerRef?.isDragging
                    && selectedUIModel.type === UIModelType.CONNECTOR) {
                    const connectorModel = selectedUIModel as IConnectorModel;
                    // 如果连接的图形也没有被选中，需要被置空
                    const startModel = connectorModel.startUIModel;
                    if (startModel && !startModel.selected) {
                        connectorModel.props.style.connectorInfo.start = undefined;
                        startModel.removeRelatedUIModelRefs(connectorModel);
                    }
                    const endModel = connectorModel.endUIModel;
                    if (endModel && !endModel.selected) {
                        connectorModel.props.style.connectorInfo.end = undefined;
                        endModel.removeRelatedUIModelRefs(connectorModel);
                    }
                }
                // eslint-disable-next-line @typescript-eslint/prefer-for-of
                for (let j = 0; j < selectedUIModel.relatedUIModelRefs.length; j++) {
                    const connectorModel = selectedUIModel.relatedUIModelRefs[j];
                    const updateSytle = connectorModel.connectorCaculater.getUpdateStyle(offsetFrame);
                    connectorModel.updateModelStyle(updateSytle);
                }
            }
        });
    };

    private clear = () => {
        this.moveOffsetFrame = null;
        this.lastSelectArray = null;
        this.currentMoveType = null;
        this.moveTimer && clearTimeout(this.moveTimer);
        this.moveTimer = null;
    };
}
